import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Row, Col, Table, Button, Input, Label, FormGroup,
    Spinner, Badge, Alert, ButtonGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../../api";
// Round DMS-UAT-3 — Document Control = admin claims OR dms.release module
// (legacy release-window parity; see visibility rules below).
import usePermissions from "../../../../hooks/usePermissions";

/**
 * Round R8 (2026) — ECN Tasks subgrid (Gap 9).
 *
 * Surfaces the legacy DMSNew.vb Tasks subgrid on the modern stack. Restores
 * the ability to attach work-items to an ECN — typically tied to Status 55
 * ("Awaiting Release, Tasks Open") and Status 56 ("Awaiting Release, Tasks
 * Complete"). Round 8 ships the data + UI; auto-status-advance (1 → 55 / 55
 * → 56) is a planned small follow-up.
 *
 * Backend endpoints (DmsController):
 *   GET    /api/dms/{id}/tasks
 *   POST   /api/dms/{id}/tasks                body: { description, dueDate, assignees }
 *   POST   /api/dms/{id}/tasks/{taskId}/complete[?assigneeEmpId=]
 *   DELETE /api/dms/{id}/tasks/{taskId}
 *
 * Visibility rules (mirrored server-side; Round DMS-UAT-3 legacy parity —
 * "Document Control" = doc author's release counterpart: dms.release
 * permission OR isDmsManager OR isItAdmin, matching the WinForms release
 * window where the releaser could assign/remove/complete tasks):
 *   • Anyone authenticated can READ.
 *   • Doc author or Document Control can ADD a task.
 *   • Assignees mark THEIR assignment complete; Document Control can mark
 *     an assignment complete on an assignee's behalf (audited).
 *   • Task creator, doc author, or Document Control can DELETE a task.
 *
 * Props:
 *   dmsId        — the DMS document id this tab belongs to.
 *   docType      — used to gate visibility upstream (ECN-only).
 *   author       — the doc's author empId, used to decide add-task button visibility.
 *   approvals    — passed through unchanged (not currently used inside but
 *                  reserved for future assignee-picker auto-suggestions).
 */
export default function EcnTasksTab({ dmsId, docType, author, approvals }) {
    const callerEmpId = useMemo(() => {
        try {
            const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
            return Number(u.uid) || 0;
        } catch { return 0; }
    }, []);

    // Roughly: "can I add or delete tasks?" — we let the server be the
    // authority (it rejects with 403 if wrong), but hiding the buttons
    // keeps the UI clean.
    const isAuthor = Number(author) === callerEmpId;
    const { canAccessModule } = usePermissions();
    const claimAdmin = useMemo(() => {
        try {
            const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
            return Boolean(u.isItAdmin || u.isDmsManager);
        } catch { return false; }
    }, []);
    // Round DMS-UAT-3 — Document Control includes the release-permission
    // holder (dms.release), matching the backend CallerIsDocumentControl()
    // and the legacy release window's task grid privileges.
    const isAdmin = claimAdmin || canAccessModule("dms.release");
    const canAddTasks = isAuthor || isAdmin;

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // Add-task form state.
    const [showAddForm, setShowAddForm] = useState(false);
    const [newDescription, setNewDescription] = useState("");
    const [newDueDate, setNewDueDate] = useState("");
    const [newAssigneeInput, setNewAssigneeInput] = useState("");
    // Round DMS-UAT-10 (owner): pick assignees from a dropdown of all active
    // employees instead of typing raw EmpIDs — same directory endpoint the
    // HR roster and the EPO form already use.
    const [employeeDir, setEmployeeDir] = useState([]);
    const [newAssignees, setNewAssignees] = useState([]);
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState(null);

    const refresh = useCallback(async () => {
        if (!dmsId) return;
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.get(`/dms/${dmsId}/tasks`);
            setTasks(res.data || []);
        } catch (err) {
            setLoadError(err?.response?.data?.error || err?.message || "Failed to load tasks.");
        } finally {
            setLoading(false);
        }
    }, [dmsId]);

    useEffect(() => {
        api.get("WhosInBuilding/userList")
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data : [];
                list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
                setEmployeeDir(list);
            })
            .catch(() => {}); refresh(); }, [refresh]);

    const resetAddForm = () => {
        setNewDescription("");
        setNewDueDate("");
        setNewAssigneeInput("");
        setNewAssignees([]);
        setAddError(null);
    };

    // Dropdown path — same dedupe/validity rules as the old manual entry.
    const handlePickAssignee = (value) => {
        if (!value) return;
        const empId = parseInt(value, 10);
        if (!Number.isFinite(empId) || empId <= 0) return;
        setNewAssignees(prev => prev.includes(empId) ? prev : [...prev, empId]);
    };

    const handleAddAssignee = () => {
        const trimmed = newAssigneeInput.trim();
        if (!trimmed) return;
        const empId = Number(trimmed);
        if (!Number.isFinite(empId) || empId <= 0) {
            setAddError("Enter a valid numeric empId.");
            return;
        }
        if (newAssignees.includes(empId)) {
            setAddError(`empId ${empId} is already in the list.`);
            return;
        }
        setNewAssignees(prev => [...prev, empId]);
        setNewAssigneeInput("");
        setAddError(null);
    };

    const handleRemoveAssignee = (empId) => {
        setNewAssignees(prev => prev.filter(x => x !== empId));
    };

    const handleAddTask = async () => {
        setAddError(null);
        const trimmedDesc = newDescription.trim();
        if (trimmedDesc.length < 3) {
            setAddError("Description must be at least 3 characters.");
            return;
        }
        if (newAssignees.length === 0) {
            setAddError("Add at least one assignee (empId).");
            return;
        }
        setAdding(true);
        try {
            const body = {
                description: trimmedDesc,
                dueDate: newDueDate || null,
                assignees: newAssignees,
            };
            const res = await api.post(`/dms/${dmsId}/tasks`, body);
            toast.success(res?.data?.message || "Task added.");
            resetAddForm();
            setShowAddForm(false);
            await refresh();
        } catch (err) {
            setAddError(err?.response?.data?.error || err?.message || "Failed to add task.");
        } finally {
            setAdding(false);
        }
    };

    // Round DMS-UAT-8 (2026) — Issue 12 completion: edit a task IN PLACE
    // (description, due date, assignees) instead of delete + re-add. Gated
    // like add/delete (author or Document Control); the backend re-checks,
    // validates assignees against the directory, never removes completed
    // assignments, keeps the 55/56 status coherent, and audits the change.
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [editDescription, setEditDescription] = useState("");
    const [editDueDate, setEditDueDate] = useState("");
    const [editAssignees, setEditAssignees] = useState([]);
    const [editAssigneeInput, setEditAssigneeInput] = useState("");
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState(null);

    const startEditTask = (t) => {
        setEditingTaskId(t.taskId);
        setEditDescription(t.description || "");
        setEditDueDate(t.dueDate ? String(t.dueDate).slice(0, 10) : "");
        setEditAssignees((t.assignees || []).map(a => Number(a.empId)));
        setEditAssigneeInput("");
        setEditError(null);
    };
    const cancelEditTask = () => {
        setEditingTaskId(null);
        setEditError(null);
    };
    const addEditAssignee = () => {
        const n = Number(String(editAssigneeInput).trim());
        if (!Number.isFinite(n) || n <= 0) return;
        setEditAssignees(prev => prev.includes(n) ? prev : [...prev, n]);
        setEditAssigneeInput("");
    };
    const removeEditAssignee = (empId) => {
        setEditAssignees(prev => prev.filter(x => x !== empId));
    };
    const saveEditTask = async () => {
        setEditError(null);
        const trimmed = editDescription.trim();
        if (trimmed.length < 3) { setEditError("Description must be at least 3 characters."); return; }
        if (editAssignees.length === 0) { setEditError("At least one assignee is required."); return; }
        setEditSaving(true);
        try {
            const body = {
                description: trimmed,
                dueDate: editDueDate || null,
                clearDueDate: !editDueDate,
                assignees: editAssignees,
            };
            const res = await api.put(`/dms/${dmsId}/tasks/${editingTaskId}`, body);
            toast.success(res?.data?.message || "Task updated.");
            setEditingTaskId(null);
            await refresh();
        } catch (err) {
            setEditError(err?.response?.data?.error || err?.message || "Failed to update task.");
        } finally {
            setEditSaving(false);
        }
    };

    const handleCompleteMyAssignment = async (taskId) => {
        try {
            const res = await api.post(`/dms/${dmsId}/tasks/${taskId}/complete`);
            toast.success(res?.data?.message || "Marked complete.");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.error || err?.message || "Failed to mark complete.");
        }
    };

    // Round DMS-UAT-3 — Document Control marks an assignment complete on the
    // assignee's behalf (legacy release-window Complete checkbox parity).
    // Confirms first (Cancel does nothing); the server re-checks the caller's
    // Document Control claims and writes an "on behalf of" audit entry.
    const handleCompleteOnBehalf = async (taskId, assignee) => {
        const who = assignee.name || assignee.empId;
        if (!window.confirm(`Mark this task complete on behalf of ${who}? This is recorded in the audit trail.`)) return;
        try {
            const res = await api.post(
                `/dms/${dmsId}/tasks/${taskId}/complete`,
                null,
                { params: { assigneeEmpId: assignee.empId } }
            );
            toast.success(res?.data?.message || `Marked complete on behalf of ${who}.`);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.error || err?.message || "Failed to mark complete.");
        }
    };

    const handleDeleteTask = async (taskId, description) => {
        if (!window.confirm(`Delete task "${description}"? This cannot be undone.`)) return;
        try {
            const res = await api.delete(`/dms/${dmsId}/tasks/${taskId}`);
            toast.success(res?.data?.message || "Task removed.");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.error || err?.message || "Failed to delete task.");
        }
    };

    // If the doc isn't ECN, this tab shouldn't render at all — but the
    // upstream tabs array already gates this, so we keep a small defensive
    // message in case the component is mounted directly.
    if (docType !== 4) {
        return (
            <Alert color="info" className="mb-0">
                Tasks are only available on ECN documents.
            </Alert>
        );
    }

    return (
        <div>
            <div className="d-flex align-items-center gap-2 mb-3">
                <div className="me-auto fw-semibold">
                    <i className="ri-list-check-2 align-bottom me-1 text-primary"></i>
                    ECN Tasks
                    {!loading && tasks.length > 0 && (
                        <Badge color="light" className="ms-2 text-dark">{tasks.length}</Badge>
                    )}
                </div>
                <Button color="light" size="sm" onClick={refresh} disabled={loading} title="Reload">
                    <i className="ri-refresh-line align-bottom"></i>
                </Button>
                {canAddTasks && !showAddForm && (
                    <Button color="primary" size="sm" outline onClick={() => setShowAddForm(true)}>
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Task
                    </Button>
                )}
            </div>

            {showAddForm && (
                <div className="border rounded p-3 mb-3 bg-light-subtle">
                    <Row className="g-2">
                        <Col md={12}>
                            <FormGroup>
                                <Label for="r8_taskDesc" className="small fw-semibold mb-1">Description</Label>
                                <Input
                                    type="textarea"
                                    id="r8_taskDesc"
                                    rows={2}
                                    value={newDescription}
                                    onChange={(e) => setNewDescription(e.target.value)}
                                    placeholder="What needs to be done?"
                                    disabled={adding}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="r8_taskDue" className="small fw-semibold mb-1">Due date (optional)</Label>
                                <Input
                                    type="date"
                                    id="r8_taskDue"
                                    value={newDueDate}
                                    onChange={(e) => setNewDueDate(e.target.value)}
                                    disabled={adding}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={8}>
                            <FormGroup>
                                <Label for="r8_taskAssignee" className="small fw-semibold mb-1">Assignee empIds</Label>
                                <div className="d-flex gap-2">
                                    <select
                                        className="form-select form-select-sm"
                                        value=""
                                        onChange={(e) => { handlePickAssignee(e.target.value); e.target.value = ""; }}
                                        disabled={adding}
                                    >
                                        <option value="">Select employee to assign…</option>
                                        {employeeDir.map((emp) => (
                                            <option key={emp.empId} value={parseInt(emp.empId, 10)}>
                                                {emp.name} ({emp.empId})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {newAssignees.length > 0 && (
                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                        {newAssignees.map(empId => (
                                            <Badge key={empId} color="info" className="d-inline-flex align-items-center gap-1">
                                                {empId}
                                                <i
                                                    className="ri-close-line"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => handleRemoveAssignee(empId)}
                                                ></i>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    {addError && (
                        <Alert color="danger" className="mb-2 mt-1 py-2">
                            <i className="ri-error-warning-line align-bottom me-1"></i>
                            {addError}
                        </Alert>
                    )}

                    <ButtonGroup>
                        <Button color="light" size="sm" onClick={() => { resetAddForm(); setShowAddForm(false); }} disabled={adding}>
                            Cancel
                        </Button>
                        <Button color="primary" size="sm" onClick={handleAddTask} disabled={adding}>
                            {adding ? (<><Spinner size="sm" /> Adding…</>) : "Add task"}
                        </Button>
                    </ButtonGroup>
                </div>
            )}

            {loading && (
                <div className="text-center py-4">
                    <Spinner color="primary" size="sm" />
                    <div className="text-muted small mt-2">Loading tasks…</div>
                </div>
            )}

            {!loading && loadError && (
                <Alert color="danger" className="mb-0">
                    <i className="ri-error-warning-line align-bottom me-1"></i>
                    {loadError}
                </Alert>
            )}

            {!loading && !loadError && tasks.length === 0 && (
                <div className="text-center py-4 text-muted">
                    <i className="ri-list-check-2" style={{ fontSize: 32 }}></i>
                    <div className="mt-2">No tasks attached to this document yet.</div>
                </div>
            )}

            {!loading && !loadError && tasks.length > 0 && (
                <div className="table-responsive">
                    <Table className="align-middle mb-0" hover>
                        <thead className="table-light">
                            <tr>
                                <th>Task</th>
                                {/* Round DMS-1 (owner request #3): the workflow tracks the
                                    ASSIGNED date, and Days Open makes turnaround visible for
                                    KPIs at a glance. Due stays — it is optional, unused by
                                    most, but an existing feature we keep. */}
                                <th>Assigned</th>
                                <th>Days Open</th>
                                <th>Due</th>
                                <th>Created by</th>
                                <th>Assignees</th>
                                <th>Progress</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tasks.map(t => {
                                const myAssignment = (t.assignees || []).find(a => Number(a.empId) === callerEmpId);
                                const canMarkMine  = myAssignment && !myAssignment.complete;
                                const canDelete    = isAdmin
                                    || isAuthor
                                    || (t.createdBy && Number(t.createdBy.empId) === callerEmpId);
                                const completedCount = (t.assignees || []).filter(a => a.complete).length;
                                const totalCount     = (t.assignees || []).length;
                                // Round DMS-UAT-8 — edit-in-place, same gate as add.
                                const canEdit = canAddTasks;
                                const isEditingThis = editingTaskId === t.taskId;
                                return (
                                    <React.Fragment key={t.taskId}>
                                    <tr>
                                        <td style={{ maxWidth: 280 }}>
                                            <div className="fw-medium">{t.description}</div>
                                            <div className="text-muted small">
                                                Added {t.creationDate ? new Date(t.creationDate).toLocaleDateString() : "—"}
                                            </div>
                                        </td>
                                        <td>
                                            {(t.assignedDate || t.creationDate)
                                                ? new Date(t.assignedDate || t.creationDate).toLocaleDateString()
                                                : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                            {Number.isFinite(Number(t.daysOpen)) ? (
                                                <span className={
                                                    "badge " + (Number(t.daysOpen) >= 30
                                                        ? "bg-danger-subtle text-danger"
                                                        : Number(t.daysOpen) >= 14
                                                            ? "bg-warning-subtle text-warning"
                                                            : "bg-success-subtle text-success")
                                                }>
                                                    {t.daysOpen} {Number(t.daysOpen) === 1 ? "day" : "days"}
                                                </span>
                                            ) : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                            {t.dueDate
                                                ? new Date(t.dueDate).toLocaleDateString()
                                                : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                            {t.createdBy?.name || <span className="text-muted">{t.createdBy?.empId || "—"}</span>}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {(t.assignees || []).map(a => {
                                                    // Round DMS-UAT-3 — legacy release-window parity:
                                                    // Document Control can tick an assignee's Complete
                                                    // (server-validated + audited). Click an incomplete
                                                    // badge to complete on their behalf.
                                                    const canCompleteOnBehalf =
                                                        isAdmin && !a.complete && Number(a.empId) !== callerEmpId;
                                                    return (
                                                        <Badge
                                                            key={a.empId}
                                                            color={a.complete ? "success" : "secondary"}
                                                            pill
                                                            style={canCompleteOnBehalf ? { cursor: "pointer" } : undefined}
                                                            onClick={canCompleteOnBehalf
                                                                ? () => handleCompleteOnBehalf(t.taskId, a)
                                                                : undefined}
                                                            title={a.complete
                                                                ? (a.completeStamp ? `Done ${new Date(a.completeStamp).toLocaleDateString()}` : "Done")
                                                                : (canCompleteOnBehalf
                                                                    ? `Pending — click to mark complete on behalf of ${a.name || a.empId} (audited)`
                                                                    : "Pending")}
                                                        >
                                                            {a.complete && <i className="ri-check-line me-1"></i>}
                                                            {a.name || a.empId}
                                                            <span className="ms-1 opacity-75">({a.empId})</span>
                                                        </Badge>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge color={t.allComplete ? "success" : "warning"} pill>
                                                {completedCount} / {totalCount}
                                            </Badge>
                                        </td>
                                        <td className="text-end">
                                            <ButtonGroup>
                                                {canMarkMine && (
                                                    <Button
                                                        color="success"
                                                        size="sm"
                                                        outline
                                                        onClick={() => handleCompleteMyAssignment(t.taskId)}
                                                        title="Mark my assignment complete"
                                                    >
                                                        <i className="ri-check-line"></i>
                                                    </Button>
                                                )}
                                                {canEdit && !isEditingThis && (
                                                    <Button
                                                        color="secondary"
                                                        size="sm"
                                                        outline
                                                        onClick={() => startEditTask(t)}
                                                        title="Edit task (description, due date, assignees)"
                                                    >
                                                        <i className="ri-pencil-line"></i>
                                                    </Button>
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        outline
                                                        onClick={() => handleDeleteTask(t.taskId, t.description)}
                                                        title="Delete task"
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </Button>
                                                )}
                                            </ButtonGroup>
                                        </td>
                                    </tr>
                                    {/* Round DMS-UAT-8 — inline edit form (Issue 12) */}
                                    {isEditingThis && (
                                        <tr className="table-light">
                                            <td colSpan={8}>
                                                <div className="border rounded p-3 bg-white">
                                                    <Row className="g-2">
                                                        <Col md={12}>
                                                            <Label className="small fw-semibold mb-1">Description</Label>
                                                            <Input
                                                                type="textarea"
                                                                rows={2}
                                                                value={editDescription}
                                                                onChange={(e) => setEditDescription(e.target.value)}
                                                                disabled={editSaving}
                                                            />
                                                        </Col>
                                                        <Col md={4}>
                                                            <Label className="small fw-semibold mb-1">Due date</Label>
                                                            <Input
                                                                type="date"
                                                                value={editDueDate}
                                                                onChange={(e) => setEditDueDate(e.target.value)}
                                                                disabled={editSaving}
                                                            />
                                                            <div className="text-muted small mt-1">Clear the date to remove the deadline.</div>
                                                        </Col>
                                                        <Col md={8}>
                                                            <Label className="small fw-semibold mb-1">Assignee empIds</Label>
                                                            <div className="d-flex gap-2">
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value=""
                                                                    onChange={(e) => { handlePickAssignee(e.target.value); e.target.value = ""; }}
                                                                    disabled={adding}
                                                                >
                                                                    <option value="">Select employee to assign…</option>
                                                                    {employeeDir.map((emp) => (
                                                                        <option key={emp.empId} value={parseInt(emp.empId, 10)}>
                                                                            {emp.name} ({emp.empId})
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <Button color="secondary" outline size="sm" onClick={addEditAssignee} disabled={editSaving}>
                                                                    <i className="ri-add-line"></i>
                                                                </Button>
                                                            </div>
                                                            {editAssignees.length > 0 && (
                                                                <div className="d-flex flex-wrap gap-1 mt-2">
                                                                    {editAssignees.map(empId => {
                                                                        const existing = (t.assignees || []).find(a => Number(a.empId) === empId);
                                                                        const done = existing?.complete;
                                                                        return (
                                                                            <Badge key={empId} color={done ? "success" : "info"} className="d-inline-flex align-items-center gap-1">
                                                                                {existing?.name || empId} ({empId})
                                                                                {done
                                                                                    ? <i className="ri-lock-line" title="Already completed — kept in the record"></i>
                                                                                    : <i
                                                                                        className="ri-close-line"
                                                                                        style={{ cursor: "pointer" }}
                                                                                        onClick={() => removeEditAssignee(empId)}
                                                                                        title="Remove assignee"
                                                                                    ></i>}
                                                                            </Badge>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                    {editError && (
                                                        <Alert color="danger" className="mb-2 mt-2 py-2">
                                                            <i className="ri-error-warning-line align-bottom me-1"></i>
                                                            {editError}
                                                        </Alert>
                                                    )}
                                                    <ButtonGroup className="mt-2">
                                                        <Button color="light" size="sm" onClick={cancelEditTask} disabled={editSaving}>
                                                            Cancel
                                                        </Button>
                                                        <Button color="primary" size="sm" onClick={saveEditTask} disabled={editSaving}>
                                                            {editSaving ? (<><Spinner size="sm" /> Saving…</>) : "Save task"}
                                                        </Button>
                                                    </ButtonGroup>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
}
